import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import Team from './columns/Team';
import Members from './columns/Members';
import TeamTableContextProvider from './context/TeamTableContext';
import NestingDropdown from './columns/NestingDropdown';

import { useTeamsTableGql } from './graphql';
import { Paginate } from '../appPaginate';
import GoalsWithNesting from './columns/GoalsWithNesting';
import { getTeamNames, getTeamParentMap } from './TeamTable.utils';
import TeamMetric from './columns/TeamMetrics';
import { handleTeamSettingsModalProps } from './teamTableTypes';
import { MetricSidebarContainer } from '../tableComponents/common/columns/MetricSidebarContainer';
import { useDashboardStore } from '../hooks/useDashboardStore';
import useTeamTableStore from '../hooks/useTeamTableStore';
import { MetricTooltip } from '../tableComponents';
import type { TeamDto } from './types/team-table.types';

type Team = {
  name: string;
  subTeams?: Team[];
};

interface MetricState {
  metricTitle: string;
  metricKey: string;
  sequence: number;
  id?: string;
}
interface TeamTableProps {
  teamId: string;
  selectedMetrics?: MetricState[];
  startDate?: string;
  endDate?: string;
  height?: string;
  handleTeamSettingsModal?: handleTeamSettingsModalProps;
  componentOrigin?: string;
  hiddenColumns?: string[];
  compareTo?: string;
  metricList?: MetricTooltip[];
  orderBy?: string;
  setOrderBy?: (orderBy: string) => void;
  orderColumn?: string;
  setOrderColumn?: (orderColumn: string) => void;
  setOrderByUrl?: (orderBy: string) => void;
}

export function TeamTable({
  teamId,
  selectedMetrics,
  startDate,
  endDate,
  height,
  handleTeamSettingsModal,
  componentOrigin,
  hiddenColumns = [],
  compareTo,
  metricList,
  orderBy,
  setOrderBy,
  orderColumn,
  setOrderColumn,
}: TeamTableProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const { selected } = useDashboardStore();
  const [totalPages, setTotalPages] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const { selectedTeams, setSelectedTeams } = useTeamTableStore();
  const { data: goalsTeamData, isFetching } = useTeamsTableGql(
    teamId,
    orderColumn ? selectedTeams : [],
    orderColumn ? 0 : currentPage
  );

  const handlePageClick = useCallback(
    (page: number) => {
      setCurrentPage(page - 1);
    },
    [currentPage]
  );
  const scrollToTop = () => {
    if (boxRef.current) {
      boxRef.current.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    }
  };

  useEffect(() => {
    scrollToTop();
    setCurrentPage(0);
  }, [orderColumn, teamId, selected]);

  // when a column where sort was applied is removed by user. set orderColumn to '' and set setSelectedTeams to []
  useEffect(() => {
    if (!selectedMetrics?.map((metric) => metric.id).includes(orderColumn)) {
      setOrderColumn?.('');
      setOrderBy?.('');
      setSelectedTeams([]);
    }
  }, [selectedMetrics]);

  useEffect(() => {
    if (goalsTeamData?.totalPages && orderColumn === '') {
      setTotalPages(goalsTeamData?.totalPages ?? 1);
    }

    if (goalsTeamData?.teamDtoList?.length === 0 && !isFetching) {
      setOrderColumn?.('');
      setOrderBy?.('');
      setSelectedTeams([]);
    }
  }, [goalsTeamData, orderColumn, isFetching]);

  const teamData =
    goalsTeamData?.teamDtoList.map((item: TeamDto) => {
      return { ...item, subTeams: item.subTeams || [] };
    }) || [];
  const membersData =
    goalsTeamData?.teamDtoList.map((item: TeamDto) => {
      return {
        ...item.teamMetaData,
        name: item.name,
        subTeams: item.subTeams,
        subTeamsList: item.teamMetaData.subTeams,
      };
    }) || [];
  // useEffect(() => {
  //   if (!orderColumn) {
  //     setSelectedTeams(getTeamNames(goalsTeamData?.teamDtoList || []));
  //   }
  // }, [goalsTeamData, orderColumn]);

  return (
    <Flex direction="column">
      <Flex
        mt={3}
        overflowX={'scroll'}
        ref={boxRef}
        overflowY={'auto'}
        border="1px solid #E0E0E0"
        height={height || '66vh'}
        width="100%"
        borderRadius="8px"
        position="relative"
      >
        <TeamTableContextProvider>
          <NestingDropdown data={teamData} isLoading={isFetching} />
          <Team
            data={teamData}
            isLoading={isFetching}
            handleTeamSettingsModal={handleTeamSettingsModal}
          />
          {!hiddenColumns.includes('Members') && (
            <Members data={membersData} isLoading={isFetching} />
          )}
          {!selectedMetrics && componentOrigin === 'goals' && (
            <GoalsWithNesting
              goalScope="TEAM"
              associatedIds={getTeamNames(goalsTeamData?.teamDtoList || [])}
              teamParentMap={getTeamParentMap(goalsTeamData?.teamDtoList) || []}
              goalsTeamDataLoading={isFetching}
            />
          )}
          {selectedMetrics &&
            selectedMetrics.length > 0 &&
            selectedMetrics.map((metric) => {
              if (!['GOALS', 'KEY_RESULTS']?.includes(metric?.metricKey)) {
                return (
                  <TeamMetric
                    key={metric.id ?? metric.metricKey}
                    metricList={metricList || []}
                    selectedMetric={metric}
                    isTeamsDataLoading={isFetching}
                    metricTooltip={
                      metricList?.filter((item: MetricTooltip) => {
                        if (item.id && metric.id) {
                          return item.id === metric.id;
                        }
                        if (item.name && metric.metricKey) {
                          return item.name === metric.metricKey;
                        }
                        return false;
                      }) || []
                    }
                    selectedMetrics={selectedMetrics}
                    teamParentMap={getTeamParentMap(goalsTeamData?.teamDtoList)}
                    startDate={startDate}
                    endDate={endDate}
                    compareTo={compareTo}
                    initialOrderBy={orderColumn === metric.id ? orderBy : ''}
                    orderColumn={orderColumn}
                    setOrderColumn={setOrderColumn}
                    setOrderByUrl={setOrderBy}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    setTotalPages={setTotalPages}
                    scrollToTop={scrollToTop}
                    teams={getTeamNames(goalsTeamData?.teamDtoList || [])}
                    teamData={teamData}
                  />
                );
              } else if (
                metric?.metricKey === 'GOALS' ||
                metric?.metricKey === 'KEY_RESULTS'
              ) {
                return (
                  <GoalsWithNesting
                    goalScope="TEAM"
                    showGoal={metric?.metricKey === 'GOALS'}
                    showKeyResult={metric?.metricKey === 'KEY_RESULTS'}
                    associatedIds={getTeamNames(
                      goalsTeamData?.teamDtoList || []
                    )}
                    teamParentMap={getTeamParentMap(goalsTeamData?.teamDtoList)}
                    goalsTeamDataLoading={isFetching}
                  />
                );
              }
              return null;
            })}
        </TeamTableContextProvider>
      </Flex>
      <Box marginLeft="-20px">
        <Paginate
          currentPage={currentPage + 1}
          totalPageCount={totalPages || 0}
          pageSize={10}
          onPageChange={(page) => {
            handlePageClick(page as number);
            scrollToTop();
          }}
        />
      </Box>
      <MetricSidebarContainer />
    </Flex>
  );
}

export default TeamTable;
